import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BoldedText,
  CompanyIcon,
  DetailCont,
  DetailsCont,
  InfoIcon,
  ReportDetailsCont,
  ReportDetailsWrapper,
  ReportName,
  ReportType,
  ResponseRateCont,
  TitleBlock,
  TitleWrapper,
  TooltipCont,
} from './ReportLayout.styles';
import Tooltip from '../../HOC/Tooltip/Tooltip';
import { useAuth } from '../../hooks/useAuth';
import {
  ReportDetailsHandlersContext,
  ReportDetailsStateContext,
  useReport,
} from '../../hooks/useReport';
import { useReportMapping } from '../../hooks/useReportMapping';
import { useReports } from '../../hooks/useReports';
import { IGetReportDetailsResp } from '../../services/reports';
import { surveyTypes } from '../../constans/forms';
import DownloadReportModal from '../DownloadReportModal/DownloadReportModal';
import ResponseRate from '../ResponseRate/ResponseRate';

type ReportLayoutProps = {
  children: React.ReactNode;
  details: IGetReportDetailsResp;
};

const ReportLayout: React.FC<ReportLayoutProps> = ({ details, children }) => {
  const {
    userRolesFromToken: { roles },
    isEBHRMember,
  } = useAuth();

  const { t } = useTranslation();

  const { getReportType } = useReports();

  const {
    data,
    meta,
    getReportDetails,
    getReportGroupComparisonDetails,
    getReportResourcesDetails,
    isLoading,
    effectData,
    openQuestions,
  } = useReport({
    reportType: details.type,
    surveyType: details.survey.type,
    isOpenComments: details.contains_open_questions_responses,
  });
  const structuredEffectData = useReportMapping(effectData);

  return (
    <>
      <ReportDetailsStateContext.Provider
        value={{
          data,
          meta,
          isLoading,
          details,
          roles,
          effectData,
          structuredEffectData,
          openQuestions,
        }}
      >
        <ReportDetailsHandlersContext.Provider
          value={{
            getReportGroupComparisonDetails,
            getReportResourcesDetails,
            getReportDetails,
          }}
        >
          <ReportDetailsCont id="ReportDetailsCont">
            <ReportDetailsWrapper>
              <TitleWrapper>
                <TitleBlock>
                  <ReportName
                    id="ReportName"
                    data-testid="reportName"
                    title={details?.survey?.name}
                  >
                    {details?.survey?.name}
                  </ReportName>
                  <DetailsCont id="DetailCont">
                    {isEBHRMember ? (
                      <DetailCont>
                        <CompanyIcon id="CompanyIcon" />
                        {`${t('report.topBar.organisation')}:`}
                        <BoldedText id="BoldedText" data-testid="reportOrganization">
                          {details.organization}
                        </BoldedText>
                      </DetailCont>
                    ) : null}
                    <DetailCont>
                      {`${t('report.topBar.reportType')}:`}
                      <ReportType id="BoldedText" data-testid="reportType">
                        {getReportType(details.type)}
                      </ReportType>
                    </DetailCont>
                    <DetailCont>
                      {`${t('report.topBar.group')}:`}
                      <BoldedText id="BoldedText" data-testid="reportGroup">
                        {details.group}
                      </BoldedText>
                    </DetailCont>
                    {details.benchmark_department && (
                      <DetailCont>
                        {`${t('report.topBar.historicalComparison')}:`}
                        <BoldedText id="BoldedText" data-testid="comparison">
                          {details?.benchmark_department}
                        </BoldedText>
                      </DetailCont>
                    )}
                    <DetailCont>
                      {`${t('report.topBar.responseRate')}:`}
                      <ResponseRateCont id="ResponseRateCont" data-testid="responseRateCont">
                        <ResponseRate
                          mode={details.responsiveness.representativeness}
                          percent={details.responsiveness.responseRate}
                          responseCount={details.responsiveness.responseCount}
                          maxResponseCount={details.responsiveness.participantCount}
                        />
                      </ResponseRateCont>
                      <TooltipCont id="TooltipCont">
                        <Tooltip
                          tooltipDesc={t('report.topBar.responseRateTooltip')}
                          positionX="-300px"
                          arrowPositionX="303px"
                        >
                          <InfoIcon />
                        </Tooltip>
                      </TooltipCont>
                    </DetailCont>
                  </DetailsCont>
                </TitleBlock>
                {/* Download temporarily disabled for GOOD_TEAM reports */}
                {details.survey.type !== surveyTypes.GOOD_TEAM ? (
                  <DownloadReportModal metadata={details} />
                ) : null}
              </TitleWrapper>
              {children}
            </ReportDetailsWrapper>
          </ReportDetailsCont>
        </ReportDetailsHandlersContext.Provider>
      </ReportDetailsStateContext.Provider>
    </>
  );
};

export default ReportLayout;
